/**
 * Creature templates (ADR-082 decisions 1, 2 and 5): a host-wide authored document, `tools/dev-server/creatures/<id>.json`,
 * checked here; the server part the compile writes into a world version as `creatures.json`, each template with its
 * `appearance`, its index in the look table; and that public look table, `creature-looks.json`, one name, character
 * sheet and temperament per template in template id order, which is all a page learns of a creature beside its level
 * (the temperament colours and marks its nameplate, R-0089). A template may name the loot table its fall rolls
 * (ADR-091's amendment for P2.8-T13, decision 2), which stays in the server part. Pure.
 */

import { SLUG_PATTERN } from "../content/slug.ts";
import { MAX_CREATURE_HEALTH } from "../rules/creature-levels.ts";
import { LOOT_ID_RULE, isLootTableId } from "../items/loot.ts";

/**
 * The most ticks a template rests between wanders, an hour at 20 ticks a second.
 */
export const MAX_REST_TICKS = 72_000;

/**
 * The most characters a template's shown name takes, a name part's limit on the wire.
 */
export const MAX_CREATURE_NAME_CHARS = 32;

/**
 * The fields a template holds, in the order its checks run.
 */
const FIELDS = ["id", "name", "look", "level", "health", "pace", "abilities", "noticeTiles", "leashTiles", "restTicks", "xp", "temperament", "threatTiles", "rank", "loot", "healsAtHome"] as const;

/**
 * How a creature meets characters (the author's ruling of 2026-10-02, ADR-082's rulings): an aggressive one notices
 * a character within its `noticeTiles`; a neutral one notices none by range and fights only those who strike it. Once a
 * character is on its threat ring, both fight it alike.
 */
export const TEMPERAMENTS = ["aggressive", "neutral"] as const;
export type Temperament = (typeof TEMPERAMENTS)[number];

/**
 * A creature's rank, which frames the level on its nameplate (the design's NPC and creature nameplates: a rank frame
 * of 1 for an elite, 2 for a boss) and weighs the death penalty it brings (ADR-079's amendment of 2026-10-04, item 2).
 * A template names one with its optional `rank`; one without reads as `standard`, and so does a look table's entry.
 */
export const CREATURE_RANKS = ["standard", "elite", "boss"] as const;
export type CreatureRank = (typeof CREATURE_RANKS)[number];

const SLUG = SLUG_PATTERN;
/**
 * The level every template holds: its numbers are the level 1 base, and a spawn marker sets the level (ADR-082's amendment of 2026-10-15).
 */
const BASE_LEVEL = 1;
const MAX_HEALTH = MAX_CREATURE_HEALTH;
const MAX_PACE = 4;
const MAX_TILES = 255;
const MAX_APPEARANCE = 65_535;

/**
 * A creature template.
 */
export interface CreatureTemplate {
  readonly id: string;
  readonly name: string;
  readonly look: string;
  readonly level: number;
  readonly health: number;
  readonly pace: number;
  readonly abilities: readonly string[];
  readonly noticeTiles: number;
  readonly leashTiles: number;
  readonly restTicks: readonly [number, number];
  readonly xp: number;
  readonly temperament: Temperament;
  readonly threatTiles: number;
  /** The rank, as the file names it; absent for a template that names none, which is `standard` (`creatureRankOf`). */
  readonly rank?: CreatureRank;
  /** The id of the loot table its fall rolls; absent for a template that drops nothing. */
  readonly loot?: string;
  /**
   * Whether its creatures heal to full, their effects cleared, once a walk home arrives (ADR-082's amendment of
   * 2026-10-09, item 1): a mechanic a template opts into. Written only when true; absent, a creature never heals itself.
   */
  readonly healsAtHome?: true;
}

/**
 * A template as a world version holds it for its map server: its index in the version's look table beside it.
 */
export interface CompiledCreatureTemplate extends CreatureTemplate {
  readonly appearance: number;
}

/**
 * One entry of a version's look table as `creature-looks.json` holds it: the name a page shows, the character sheet it
 * draws and the temperament its nameplate shows.
 */
export interface CreatureLookEntry {
  readonly name: string;
  readonly sheet: string;
  readonly temperament: Temperament;
  /** The template's id, which names its sound folder (ADR-085 decision 3); absent in a table compiled before it. */
  readonly template?: string;
  /** The template's rank, written only when its template names one; absent reads as `standard`. */
  readonly rank?: CreatureRank;
}

/**
 * One entry of a version's look table as a page reads it: the file's entry and the rank that frames the level.
 */
export interface CreatureLook extends Omit<CreatureLookEntry, "rank"> {
  readonly rank: CreatureRank;
}

/**
 * The verdict on one template document.
 */
export type CreatureTemplateVerdict = { readonly ok: true; readonly template: CreatureTemplate } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Whether a value is an integer within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function integerIn(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Checks a creature template as its file holds it, every field in the order of ADR-082 decision 2's table, each
 * error naming the template and the field. `temperament` may be left out and is then `aggressive`, `threatTiles`
 * (how far from the creature a character on its threat ring stays on it) may be left out and is then its
 * `leashTiles`, `rank` may be left out and is then `standard` (kept absent, so the compile writes what it wrote
 * before the field), and `loot` may be left out for a template that drops nothing; its id's form is checked here and
 * the table itself by the compile. A version compiled before the fields reads as it always behaved. `healsAtHome` may
 * be left out or false and is then kept absent, so the compile of a template without it writes the bytes it wrote
 * before. `level` must be 1: a template holds the level 1 base and a spawn marker sets the level.
 *
 * @param value The parsed file.
 * @param fileId The file's name without `.json`, which the `id` must equal.
 * @param topPercent The health percent of the creature table's last level (`topHealthPercent`), which bounds a template's health.
 * @returns The template, or every error found.
 * @example
 * validateCreatureTemplate(JSON.parse(text), "mutant-rat", 500);
 */
export function validateCreatureTemplate(value: unknown, fileId: string, topPercent: number): CreatureTemplateVerdict {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, errors: [`${fileId}: a template must be a JSON object`] };
  }
  const record = value as Readonly<Record<string, unknown>>;
  const errors: string[] = [];
  /**
   * Records one error against the template.
   *
   * @param message What is wrong with which field.
   */
  const fail = (message: string): void => {
    errors.push(`${fileId}: ${message}`);
  };
  const id = record["id"];
  if (typeof id !== "string" || !SLUG.test(id)) {
    fail("id must be lower-case letters, digits and -");
  } else if (id !== fileId) {
    fail(`id must be ${fileId}, the file's name, not ${id}`);
  }
  const name = record["name"];
  if (typeof name !== "string" || name.length > MAX_CREATURE_NAME_CHARS) {
    fail(`name must be text of at most ${MAX_CREATURE_NAME_CHARS} characters, empty for none`);
  }
  if (typeof record["look"] !== "string" || !SLUG.test(record["look"])) {
    fail("look must name a character sheet in lower-case letters, digits and -");
  }
  if (record["level"] !== BASE_LEVEL) {
    fail(`level must be ${BASE_LEVEL}: a template holds the level 1 base, and a spawn marker sets the level`);
  }
  if (!integerIn(record["health"], 1, MAX_HEALTH)) {
    fail(`health must be an integer from 1 to ${MAX_HEALTH}`);
  } else {
    const largest = Math.floor((MAX_CREATURE_HEALTH * 100) / topPercent);
    if ((record["health"] as number) > largest) {
      fail(`health ${record["health"] as number} would pass ${MAX_CREATURE_HEALTH} at the highest creature level; the most is ${largest}`);
    }
  }
  if (!integerIn(record["pace"], 1, MAX_PACE)) {
    fail(`pace must be an integer from 1 to ${MAX_PACE}`);
  }
  const abilities = record["abilities"];
  if (!Array.isArray(abilities) || abilities.some((ability) => typeof ability !== "string" || ability === "") || new Set(abilities).size !== abilities.length) {
    fail("abilities must be a list of distinct non-empty ability ids");
  }
  if (!integerIn(record["noticeTiles"], 1, MAX_TILES)) {
    fail(`noticeTiles must be an integer from 1 to ${MAX_TILES}`);
  }
  if (!integerIn(record["leashTiles"], 1, MAX_TILES)) {
    fail(`leashTiles must be an integer from 1 to ${MAX_TILES}`);
  }
  const rest = record["restTicks"];
  if (!Array.isArray(rest) || rest.length !== 2 || !integerIn(rest[0], 0, MAX_REST_TICKS) || !integerIn(rest[1], rest[0] as number, MAX_REST_TICKS)) {
    fail(`restTicks must be [min, max], integers with 0 <= min <= max <= ${MAX_REST_TICKS}`);
  }
  if (!integerIn(record["xp"], 0, Number.MAX_SAFE_INTEGER)) {
    fail("xp must be a whole number of at least 0");
  }
  const threatTiles = record["threatTiles"] ?? record["leashTiles"];
  if (!integerIn(threatTiles, 1, MAX_TILES)) {
    fail(`threatTiles must be an integer from 1 to ${MAX_TILES}`);
  }
  const temperament = record["temperament"] ?? "aggressive";
  if (!(TEMPERAMENTS as readonly unknown[]).includes(temperament)) {
    fail(`temperament must be ${TEMPERAMENTS.join(" or ")}`);
  }
  const rank = record["rank"];
  if (rank !== undefined && !(CREATURE_RANKS as readonly unknown[]).includes(rank)) {
    fail(`rank must be ${CREATURE_RANKS.slice(0, -1).join(", ")} or ${CREATURE_RANKS[CREATURE_RANKS.length - 1] as string}`);
  }
  const loot = record["loot"];
  if (loot !== undefined && (typeof loot !== "string" || !isLootTableId(loot))) {
    fail(`loot must name a loot table: ${LOOT_ID_RULE}`);
  }
  const healsAtHome = record["healsAtHome"];
  if (healsAtHome !== undefined && typeof healsAtHome !== "boolean") {
    fail("healsAtHome must be true or false");
  }
  for (const key of Object.keys(record)) {
    if (!(FIELDS as readonly string[]).includes(key)) {
      fail(`${key} is not a template field`);
    }
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  const restTicks = rest as readonly number[];
  const read: CreatureTemplate = {
    id: id as string,
    name: name as string,
    look: record["look"] as string,
    level: record["level"] as number,
    health: record["health"] as number,
    pace: record["pace"] as number,
    abilities: [...(abilities as readonly string[])],
    noticeTiles: record["noticeTiles"] as number,
    leashTiles: record["leashTiles"] as number,
    restTicks: [restTicks[0] as number, restTicks[1] as number],
    xp: record["xp"] as number,
    temperament: temperament as Temperament,
    threatTiles: threatTiles as number,
  };
  const ranked = rank === undefined ? read : { ...read, rank: rank as CreatureRank };
  const looted = loot === undefined ? ranked : { ...ranked, loot: loot as string };
  return { ok: true, template: healsAtHome === true ? { ...looted, healsAtHome: true } : looted };
}

/**
 * A template's rank: the one it names, or `standard` for one that names none.
 *
 * @param template The template.
 * @returns Its rank.
 */
export function creatureRankOf(template: Pick<CreatureTemplate, "rank">): CreatureRank {
  return template.rank ?? "standard";
}

/**
 * The templates a version holds in look table order, template id order, each with its index there.
 *
 * @param templates The templates the world's spawns name, each once.
 * @returns The compiled templates.
 */
export function compileCreatureTemplates(templates: readonly CreatureTemplate[]): CompiledCreatureTemplate[] {
  return [...templates].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map((template, appearance) => ({ ...template, appearance }));
}

/**
 * The public look table of a version: one name, sheet, temperament and template id per template, and its rank when it
 * names one, in template id order, so a template's `appearance` in `compileCreatureTemplates` indexes it.
 *
 * @param templates The templates the world's spawns name, each once.
 * @returns The table as `creature-looks.json` holds it.
 */
export function creatureLooksOf(templates: readonly CreatureTemplate[]): { readonly looks: readonly CreatureLookEntry[] } {
  return {
    looks: compileCreatureTemplates(templates).map((template) => {
      const entry = { name: template.name, sheet: template.look, temperament: template.temperament, template: template.id };
      return template.rank === undefined ? entry : { ...entry, rank: template.rank };
    }),
  };
}

/**
 * Reads a version's `creatures.json`, checking each template as its source was checked and its appearance.
 *
 * @param value The parsed file.
 * @param topPercent The health percent of the creature table's last level (`topHealthPercent`).
 * @returns The templates in the file's order.
 * @throws Error naming the template and field when one is malformed.
 */
export function parseCompiledCreatures(value: unknown, topPercent: number): CompiledCreatureTemplate[] {
  const templates = typeof value === "object" && value !== null ? (value as { templates?: unknown }).templates : undefined;
  if (!Array.isArray(templates)) {
    throw new Error("creatures.json must hold a templates list");
  }
  return templates.map((entry: unknown, i): CompiledCreatureTemplate => {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      throw new Error(`creatures.json: templates[${i}] must be a JSON object`);
    }
    const { appearance, ...rest } = entry as Readonly<Record<string, unknown>>;
    const fileId = typeof rest["id"] === "string" ? rest["id"] : `templates[${i}]`;
    const verdict = validateCreatureTemplate(rest, fileId, topPercent);
    if (!verdict.ok) {
      throw new Error(verdict.errors.join("; "));
    }
    if (!integerIn(appearance, 0, MAX_APPEARANCE)) {
      throw new Error(`creatures.json: ${fileId}'s appearance must be an integer from 0 to ${MAX_APPEARANCE}`);
    }
    return { ...verdict.template, appearance };
  });
}

/**
 * Reads a version's `creature-looks.json`. An entry without a temperament reads as `aggressive` and one without a rank
 * as `standard`, so a page meets a version compiled before either field as that version's map server runs it.
 *
 * @param value The parsed file.
 * @returns The looks, indexed by a creature's `appearance`.
 * @throws Error when the file is not a list of names and sheets, an entry names a temperament or rank not known, or a
 *   template id is not text.
 */
export function parseCreatureLooks(value: unknown): CreatureLook[] {
  const looks = typeof value === "object" && value !== null ? (value as { looks?: unknown }).looks : undefined;
  if (!Array.isArray(looks)) {
    throw new Error("creature-looks.json must hold a looks list");
  }
  return looks.map((entry: unknown, i): CreatureLook => {
    const record = typeof entry === "object" && entry !== null ? (entry as Readonly<Record<string, unknown>>) : {};
    if (typeof record["name"] !== "string" || typeof record["sheet"] !== "string") {
      throw new Error(`creature-looks.json: looks[${i}] must hold a name and a sheet`);
    }
    const temperament = record["temperament"] ?? "aggressive";
    if (!(TEMPERAMENTS as readonly unknown[]).includes(temperament)) {
      throw new Error(`creature-looks.json: looks[${i}]'s temperament must be ${TEMPERAMENTS.join(" or ")}`);
    }
    const rank = record["rank"] ?? "standard";
    if (!(CREATURE_RANKS as readonly unknown[]).includes(rank)) {
      throw new Error(`creature-looks.json: looks[${i}]'s rank must be standard, elite or boss`);
    }
    const read = { name: record["name"], sheet: record["sheet"], temperament: temperament as Temperament, rank: rank as CreatureRank };
    const template = record["template"];
    if (template === undefined) {
      return read;
    }
    if (typeof template !== "string") {
      throw new Error(`creature-looks.json: looks[${i}]'s template must be text`);
    }
    return { ...read, template };
  });
}
